import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ControlledTabs } from 'components/Form';
import { FormulaEditor, useEditorParseData } from 'components/FormulaEditor';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import type { ProjectMethodologyEnum } from 'gql/graphql';
import { OtherCostCalculationTypeEnum } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { getOtherFormulaSourceObjectGql } from 'pages/Project/Common/Contract/contractQueries';
import type { TypeItem } from 'pages/Project/Common/Contract/OtherCreate/commonData';
import { TYPE_VARIANTS } from 'pages/Project/Common/Contract/OtherCreate/commonData';
import { GridItem, Text } from 'uikit';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import {
    CatalogCostModalTab,
    catalogCostModalTabs,
    emptyCostTypeOptionId,
    formulaUnavailableText,
    getCalculatingTypeFromItem,
    getModalTitle,
    getOthersDuplicateNameError,
    getSectionFromItem,
    getSectionItems,
    limitedDuplicateNameError,
    methodologyItems,
} from '../../catalogOtherAndLimitedHelpers';
import { useIsUniqueCatalogCost } from '../../hooks/useIsUniqueCatalogCost';
import { useSaveCatalogCost } from '../../hooks/useSaveCatalogCost';
import type {
    TCatalogCostFormValues,
    TCatalogOtherAndLimitedId,
    TCatalogOtherAndLimitedItem,
    TCatalogSectionItem,
} from '../../types';
import { CatalogOtherAndLimitedTab } from '../../types';

import { CatalogCostModalCommonTab } from './CatalogCostModalCommonTab';

import Styles from './createOrEditCatalogCostModal.module.css';

const invisibleStyle = {
    visibility: 'hidden' as const,
    overflow: 'hidden' as const,
    position: 'absolute' as const,
    maxHeight: 0,
};

const defaultValues: TCatalogCostFormValues = {
    name: '',
    methodology: methodologyItems[0],
    costType: null,
    formula: null,
};

export type TCreateOrEditCatalogCostModalProps = {
    id: TCatalogOtherAndLimitedId;
    tab: CatalogOtherAndLimitedTab;
    methodology: ProjectMethodologyEnum;
    item?: TCatalogOtherAndLimitedItem;
    isAdditionalTransport?: boolean;
    onClose: () => void;
    onSaveSuccess: () => void;
};

export const CreateOrEditCatalogCostModal = ({
    id,
    tab,
    methodology: defaultMethodology,
    item,
    isAdditionalTransport = false,
    onClose,
    onSaveSuccess,
}: TCreateOrEditCatalogCostModalProps) => {
    const isCreating = id === null;
    const isOthersTab = tab === CatalogOtherAndLimitedTab.Others;
    const title = getModalTitle(tab, isCreating, isAdditionalTransport);
    const sectionItems = getSectionItems(tab, isAdditionalTransport);

    const selectedMethodologyItem = useMemo(
        () =>
            methodologyItems.find((methodologyItem) => methodologyItem.id === defaultMethodology) ??
            methodologyItems[0],
        [defaultMethodology],
    );
    const createDefaultValues = useMemo(
        () => ({
            ...defaultValues,
            methodology: selectedMethodologyItem,
        }),
        [selectedMethodologyItem],
    );

    const form = useForm<TCatalogCostFormValues>({
        criteriaMode: 'all',
        shouldUnregister: false,
        defaultValues: createDefaultValues as any,
    });
    const {
        control,
        handleSubmit,
        reset,
        getValues,
        setValue,
        setError,
        clearErrors,
        formState: { isDirty },
    } = form;

    const [activeTab, setActiveTab] = useState(catalogCostModalTabs[0]);
    const [selectedType, setSelectedType] = useState<TypeItem>(TYPE_VARIANTS[0]);
    const [selectedSection, setSelectedSection] = useState<TCatalogSectionItem>(sectionItems[0]);

    const methodology = useWatch({ control, name: 'methodology' });
    const formulaValue = useWatch({ control, name: 'formula' });
    const nameValue = useWatch({ control, name: 'name' });
    const costTypeValue = useWatch({ control, name: 'costType' });
    const isPercent = selectedType.id === OtherCostCalculationTypeEnum.Percent;
    const isFormulaRequired = !isOthersTab || isPercent;
    const formulaQueryVariables = useMemo(
        () =>
            methodology?.id
                ? {
                      methodology: methodology.id,
                      globalOnly: true,
                      take: 1000,
                      skip: 0,
                  }
                : undefined,
        [methodology?.id],
    );
    const { deserializeFromBackend, serializeStringForBackend, isSourceDataLoading } = useEditorParseData({
        getObjectSourceQuery: getOtherFormulaSourceObjectGql,
        queryVariables: formulaQueryVariables,
        withEffect: Boolean(methodology?.id),
        methodology: methodology?.id,
    });
    const previousMethodologyIdRef = useRef(methodology?.id);

    const { isUnique, isUniqueStatus, isUniqueFetching } = useIsUniqueCatalogCost({
        tab,
        id: item?.id,
        name: nameValue ?? '',
        methodology: methodology?.id,
        section: selectedSection.id,
        calculatingType: selectedType.id,
    });

    const closeAndResetModal = useCallback(() => {
        reset(defaultValues);
        onClose();
    }, [onClose, reset]);

    const handleSaveSuccess = useCallback(() => {
        closeAndResetModal();
        onSaveSuccess();
    }, [closeAndResetModal, onSaveSuccess]);

    const { save, isSaving } = useSaveCatalogCost({
        tab,
        onSuccess: handleSaveSuccess,
    });

    const fieldsToCheck = useMemo(
        () => ({
            [CatalogCostModalTab.Common]: ['name', 'costType'],
            [CatalogCostModalTab.Formula]: isFormulaRequired ? ['formula'] : [],
        }),
        [isFormulaRequired],
    );

    useEffect(() => {
        if (previousMethodologyIdRef.current && previousMethodologyIdRef.current !== methodology?.id) {
            setValue('costType', null, { shouldDirty: true });
        }

        previousMethodologyIdRef.current = methodology?.id;
    }, [methodology?.id, setValue]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: closeAndResetModal,
        });

    useEffect(() => {
        if (isCreating || !item) {
            setSelectedType(isOthersTab ? TYPE_VARIANTS[0] : TYPE_VARIANTS[1]);
            setSelectedSection(getSectionItems(tab, isAdditionalTransport)[0]);
            reset(createDefaultValues);

            return;
        }

        setSelectedType(getCalculatingTypeFromItem(item));
        setSelectedSection(getSectionFromItem(tab, item));
        reset({
            name: item.name,
            methodology: selectedMethodologyItem,
            costType: item.typeUniqueId ? { id: item.typeUniqueId, name: item.typeName } : null,
            formula: item.formula ? deserializeFromBackend(item.formula) : null,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createDefaultValues, id, isCreating, item, reset, tab, isAdditionalTransport, isOthersTab]);

    useEffect(() => {
        if (!nameValue?.trim() || isUniqueStatus !== RequestStatus.SUCCESS) {
            return;
        }

        if (isUnique === false) {
            setError('name', {
                message: isOthersTab
                    ? getOthersDuplicateNameError({
                          name: nameValue.trim(),
                          calculatingType: selectedType.id,
                          sectionLabel: selectedSection.label,
                          methodologyLabel: methodology?.label ?? '',
                      })
                    : limitedDuplicateNameError,
            });

            return;
        }

        clearErrors('name');
    }, [
        clearErrors,
        isOthersTab,
        isUnique,
        isUniqueStatus,
        methodology?.label,
        nameValue,
        selectedSection.label,
        selectedType.id,
        setError,
    ]);

    const onCancel = useCallback(() => {
        if (isDirty) {
            handleOpenConfirmationDialog();
        } else {
            closeAndResetModal();
        }
    }, [closeAndResetModal, handleOpenConfirmationDialog, isDirty]);

    const handleTabChange = useCallback(({ value }: { value: (typeof catalogCostModalTabs)[number] }) => {
        setActiveTab(value);
    }, []);

    const onSelectedTypeChange = useCallback(
        ({ value }: { value: TypeItem }) => {
            setSelectedType(value);
            setValue('formula', null, { shouldDirty: true });
        },
        [setValue],
    );

    const onSelectedSectionChange = useCallback(({ value }: { value: TCatalogSectionItem }) => {
        setSelectedSection(value);
    }, []);

    const formulaTabStyle = useMemo(
        () => (activeTab.id === CatalogCostModalTab.Formula ? undefined : invisibleStyle),
        [activeTab.id],
    );

    const formulaText = typeof formulaValue === 'string' ? formulaValue : '';
    const isFormulaMissing = isFormulaRequired && !formulaText.trim();
    const isConfirmDisabled =
        !nameValue?.trim() ||
        !costTypeValue ||
        costTypeValue.id === emptyCostTypeOptionId ||
        isFormulaMissing ||
        (!isCreating && !isDirty) ||
        isUnique === false ||
        isUniqueFetching ||
        isSaving ||
        (isFormulaRequired && isSourceDataLoading);

    const onSubmit = useCallback(
        (data: TCatalogCostFormValues) => {
            const name = data.name?.trim() ?? '';

            if (!name) {
                setActiveTab(catalogCostModalTabs[0]);
                setError('name', { message: 'Обязательное поле' });

                return;
            }

            if (!data.costType || data.costType.id === emptyCostTypeOptionId) {
                setActiveTab(catalogCostModalTabs[0]);
                setError('costType', { message: 'Обязательное поле' });

                return;
            }

            const serializedFormula = serializeStringForBackend((data.formula as string) ?? '') ?? '';

            if (isFormulaRequired && !serializedFormula.trim()) {
                setActiveTab(catalogCostModalTabs[1]);
                setError('formula', { message: 'Обязательное поле' });

                return;
            }

            if (isUnique === false) {
                setActiveTab(catalogCostModalTabs[0]);

                return;
            }

            save({
                id: item?.id,
                name,
                methodology: data.methodology.id,
                formula: isFormulaRequired ? serializedFormula : '',
                typeUniqueId: String(data.costType.id),
                calculatingType: isOthersTab ? selectedType.id : OtherCostCalculationTypeEnum.Percent,
                sectionId: selectedSection.id,
                isAdditionalTransport: isOthersTab ? undefined : isAdditionalTransport,
            });
        },
        [
            isAdditionalTransport,
            isFormulaRequired,
            isOthersTab,
            isUnique,
            item?.id,
            save,
            selectedSection.id,
            selectedType.id,
            serializeStringForBackend,
            setError,
        ],
    );

    return (
        <ModalDialog
            title={title}
            isOpen
            hasOverlay
            withTitleCloseButton
            onClose={onCancel}
            onConfirm={handleSubmit(onSubmit)}
            btnConfirmLabel={isCreating ? 'Создать' : 'Сохранить'}
            btnConfirmDisabled={isConfirmDisabled}
            style={{ width: 644 }}
            isOnClickOutsideDisable
        >
            <FormProvider {...form}>
                <div className={Styles.innerBlock}>
                    <ControlledTabs
                        value={activeTab}
                        onChange={handleTabChange}
                        items={catalogCostModalTabs}
                        size="s"
                        className={Styles.tabs}
                        control={control as any}
                        fieldsToCheck={fieldsToCheck}
                    />
                    <form>
                        <CatalogCostModalCommonTab
                            activeTabId={activeTab.id}
                            tab={tab}
                            isCreating={isCreating}
                            isAdditionalTransport={isAdditionalTransport}
                            control={control}
                            getValues={getValues}
                            selectedType={selectedType}
                            selectedSection={selectedSection}
                            currentItemId={item?.id}
                            currentTypeUniqueId={item?.typeUniqueId}
                            onSelectedTypeChange={onSelectedTypeChange}
                            onSelectedSectionChange={onSelectedSectionChange}
                        />
                        <GridItem style={formulaTabStyle}>
                            {isFormulaRequired ? (
                                <FormulaEditor
                                    name="formula"
                                    control={control}
                                    getObjectSourceQuery={getOtherFormulaSourceObjectGql}
                                    queryVariables={formulaQueryVariables}
                                    tooltipTitle="Описание расчёта"
                                    tooltipTitleClassName={Styles.formulaTitle}
                                    isRequired
                                    rules={{ required: 'Обязательное поле' }}
                                    isTooltipHidden={!!formulaTabStyle}
                                    methodology={methodology?.id}
                                    isAvailableWithEmptyVariables
                                    iconTitleSize="s"
                                />
                            ) : (
                                <Text view="secondary" as="span" className={Styles.formulaUnavailableText}>
                                    {formulaUnavailableText}
                                </Text>
                            )}
                        </GridItem>
                    </form>
                </div>
            </FormProvider>
            <ConfirmationDialog
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить? Введенные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </ModalDialog>
    );
};

CreateOrEditCatalogCostModal.displayName = 'CreateOrEditCatalogCostModal';
